import React, { FC, ReactNode } from 'react';

import { FormattedMessage, FormattedNumber } from 'react-intl';
import { DefaultTheme } from 'styled-components';

import { ShortCodeEnums } from '@nhl/nhl-breakaway-ui';

import { parseValueFromLocalizedAttribute } from '~/core/utils';

import { Goal, RosterSpot } from './types';

export const formatAsPercentage = (stat: number): ReactNode => (
    <FormattedNumber value={stat} style="unit" unit="percent" maximumFractionDigits={1} />
);

export const getEntityBackgroundColor = (
    theme: DefaultTheme,
    isHome?: boolean,
    teamAbbrev?: ShortCodeEnums.TeamTriCode
) => {
    if (!teamAbbrev) {
        return theme.Text.primary;
    }

    if (isHome) {
        return theme.Team[teamAbbrev]?.gamecenter || theme.Text.primary;
    }

    return theme.Surface.primary;
};

export const getEntityTextAndBorderColor = (
    theme: DefaultTheme,
    isHome?: boolean,
    teamAbbrev?: ShortCodeEnums.TeamTriCode
) => {
    if (!teamAbbrev) {
        return theme.Text.primary;
    }

    if (isHome) {
        return theme.Team[teamAbbrev]?.textOnGamecenter || theme.Surface.primary;
    }

    return theme.Team[teamAbbrev]?.gamecenter || theme.Text.primary;
};

export const getEntityMarkerBorder = (theme: DefaultTheme, teamAbbrev?: ShortCodeEnums.TeamTriCode, isHome?: boolean) =>
    isHome
        ? getEntityBackgroundColor(theme, isHome, teamAbbrev)
        : getEntityTextAndBorderColor(theme, isHome, teamAbbrev);

export const FormattedPlayer: FC<{ player: RosterSpot | Goal }> = ({ player }) => (
    <FormattedMessage
        description="A message with the players name and their sweater number. Ex: John Doe #88"
        defaultMessage="{firstName} {lastName}{sweaterNumber}"
        values={{
            firstName: parseValueFromLocalizedAttribute(player.firstName),
            lastName: parseValueFromLocalizedAttribute(player.lastName),
            sweaterNumber: player.sweaterNumber ? ` #${player.sweaterNumber}` : '',
        }}
    />
);

export const FormatGoalScorer: FC<{
    goalScorer: React.ReactNode;
    isShootoutEvent?: boolean;
    totalGoals?: number;
}> = ({ goalScorer, isShootoutEvent, totalGoals }) => (
    <FormattedMessage
        description="A message describing the player who scored a goal along with their season total in parentheses. Ex. Mike Smith #20 (17)"
        defaultMessage="{goalScorer}{goalScorerTotal}"
        values={{
            goalScorer,
            goalScorerTotal: isShootoutEvent ? '' : ` (${totalGoals})`,
        }}
    />
);
